import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { t } from '@lingui/macro'

import { Button, Divider, List, ListItem, ListItemButton, ListItemText, Paper, Stack, Typography } from '@mui/material'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'

import PageContainer from '@/components/ui/containers/PageContainer'

import { useGetMyNotificationsQuery } from '@/core/services/auth'
import { useGetTimeTrackingConsentQuery, useGetTimeTrackingNotificationsQuery, useMarkTimeTrackingNotificationReadMutation } from '@/core/api'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { isStaff } from '@/utils/type-guards'

import { NotificationItem } from '@/layouts/main/ui/Header/Notifications'

export default function NotificationsPage() {
  const { t: tI18n } = useTranslation()
  const navigate = useNavigate()

  const authUser = useAppSelector(selectAuthUser)
  const isStaffMember = isStaff(authUser)

  const [pagination, setPagination] = useState({ cacheKey: 'page-notifications', limit: 10, skip: 0 })

  const { events, totalEvents } = useGetMyNotificationsQuery(pagination, {
    selectFromResult: ({ data }) => ({
      events: data?.items ?? [],
      totalEvents: data?.totalCount ?? 0,
    }),
  })

  // Time tracking notifications
  const { data: consent } = useGetTimeTrackingConsentQuery(undefined, { skip: !isStaffMember })
  const skipTimeTracking = !isStaffMember || !consent?.enabled
  const { data: timeTrackingNotifications = [] } = useGetTimeTrackingNotificationsQuery(undefined, { skip: skipTimeTracking })
  const [markTimeTrackingAsRead] = useMarkTimeTrackingNotificationReadMutation()

  return (
    <PageContainer maxWidth="md">
      <Paper>
        <Stack height="100%">
          <Typography variant="h6" component="div" align="center" sx={{ p: 1 }}>
            {tI18n('page.notifications.title')}
          </Typography>
          <Stack flex={1} sx={{ overflowX: 'hidden', overflowY: 'auto' }}>
            {/* Time tracking notifications */}
            {timeTrackingNotifications.length > 0 && (
              <>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 2, pt: 1, pb: 0.5 }}>
                  <AccessTimeRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {t`Time Tracking`}
                  </Typography>
                </Stack>
                <List disablePadding>
                  {timeTrackingNotifications.map((n) => (
                    <ListItem key={n._id} disablePadding>
                      <ListItemButton
                        onClick={async () => {
                          await markTimeTrackingAsRead(n._id).unwrap().catch(() => {})
                          navigate('/time-tracking')
                        }}
                        sx={[!n.read && { bgcolor: 'action.hover' }]}
                      >
                        <ListItemText
                          primary={n.message}
                          secondary={new Date(n.createdAt).toLocaleString()}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
                <Divider />
              </>
            )}
            {/* General notifications */}
            <List disablePadding>
              {events.map((event) => (
                <NotificationItem key={event._id} event={event} />
              ))}
            </List>
          </Stack>
          {events.length < totalEvents && (
            <Stack alignItems="center" justifyContent="center" py={1}>
              <Button
                onClick={() => {
                  setPagination((prevPagination) => ({ ...prevPagination, skip: prevPagination.skip + prevPagination.limit }))
                }}
                color="primary"
              >
                {tI18n('page.notification.loadMoreButton.label')}
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>
    </PageContainer>
  )
}
